package com.evoapps.evoshift.ui.workplaces

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material.icons.filled.EventNote
import androidx.compose.material.icons.filled.Restore
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.ContractType
import com.evoapps.evoshift.domain.model.RequestSettings
import com.evoapps.evoshift.domain.model.RequestSettingsState
import com.evoapps.evoshift.domain.requests.RequestSettingsGroup
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.viewmodels.SettingsEvent
import com.evoapps.evoshift.viewmodels.typesMissingContract
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSectionLabel
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.ErrorOnNavy
import com.evoapps.evoshift.widgets.EvoCheckboxRow
import com.evoapps.evoshift.widgets.EvoConfirmDialog
import com.evoapps.evoshift.widgets.EvoDialog
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.request_type_contracts_none
import evoshift.composeapp.generated.resources.request_type_open
import evoshift.composeapp.generated.resources.request_types_dialog_hint
import evoshift.composeapp.generated.resources.request_types_for
import evoshift.composeapp.generated.resources.request_types_restore
import evoshift.composeapp.generated.resources.request_types_restore_text
import evoshift.composeapp.generated.resources.request_types_restore_title
import evoshift.composeapp.generated.resources.request_types_state
import evoshift.composeapp.generated.resources.save
import evoshift.composeapp.generated.resources.settings_request_types
import org.jetbrains.compose.resources.stringResource

/** Where a ticked type's contract line starts: under its label, past the checkbox. */
private val UnderLabel = 38.dp

/**
 * SH-263: the request types' dialog (the settings card's Edit): a checkbox per type, grouped as
 * the new-request picker groups them; under a ticked type the contracts it is for, which open its
 * settings ([RequestTypeDetail], over this dialog). "Restore recommended" and every change touch
 * [draft] only, the view model's copy: Save keeps them, Cancel drops them. Save waits while a
 * ticked type is offered on no contract.
 */
@Composable
fun RequestTypesDialog(
    draft: RequestSettings,
    customContractTypes: List<ContractType>,
    onEvent: (SettingsEvent) -> Unit
) {
    val columns = contractColumns(customContractTypes)
    var confirmRestore by remember { mutableStateOf(false) }
    val missing = draft.typesMissingContract()

    EvoDialog(
        title = stringResource(Res.string.settings_request_types),
        icon = Icons.Default.EventNote,
        onDismiss = { onEvent(SettingsEvent.CloseRequestTypes) },
        width = DialogWidth.Regular,
        confirm = DialogAction(stringResource(Res.string.save), { onEvent(SettingsEvent.SaveRequestTypes) }, enabled = missing.isEmpty())
    ) {
        Column(Modifier.fillMaxWidth().testTag(RequestTypeRuleTags.DIALOG), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(
                stringResource(Res.string.request_types_dialog_hint),
                style = MaterialTheme.typography.bodyMedium,
                color = Color.White.copy(alpha = 0.85f)
            )
            Row(Modifier.fillMaxWidth().padding(top = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                Text(
                    stringResource(Res.string.request_types_state, draft.state.label()),
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = Color.White,
                    modifier = Modifier.weight(1f).testTag(RequestTypeRuleTags.STATE)
                )
                if (draft.state != RequestSettingsState.RECOMMENDED) {
                    TextButton(onClick = { confirmRestore = true }, modifier = Modifier.handCursor().testTag(RequestTypeRuleTags.RESTORE)) {
                        Icon(Icons.Default.Restore, contentDescription = null, tint = LightGold, modifier = Modifier.size(18.dp))
                        Spacer(Modifier.width(6.dp))
                        Text(stringResource(Res.string.request_types_restore), color = LightGold, fontWeight = FontWeight.Bold)
                    }
                }
            }
            RequestSettingsGroup.entries.forEach { group ->
                DialogSectionLabel(stringResource(group.labelRes()), Modifier.padding(top = 12.dp).semantics { heading() })
                group.types.forEach { info ->
                    val name = info.type.name
                    val label = info.label()
                    val rule = draft.ruleOf(name)
                    EvoCheckboxRow(
                        checked = rule.enabled,
                        onCheckedChange = { onEvent(SettingsEvent.DraftRequestTypeChecked(name, it)) },
                        label = label,
                        modifier = Modifier.testTag(RequestTypeRuleTags.check(name))
                    )
                    if (rule.enabled) {
                        val openLabel = stringResource(Res.string.request_type_open, label)
                        Row(
                            Modifier
                                .fillMaxWidth()
                                .heightIn(min = 40.dp)
                                .handCursor()
                                .clickable(onClickLabel = openLabel) { onEvent(SettingsEvent.OpenRequestType(name)) }
                                .testTag(RequestTypeRuleTags.row(name))
                                .padding(start = UnderLabel, bottom = 4.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                stringResource(Res.string.request_types_for, contractsSummary(rule, columns)),
                                style = MaterialTheme.typography.bodySmall,
                                fontWeight = FontWeight.SemiBold,
                                color = LightGold,
                                maxLines = 2,
                                overflow = TextOverflow.Ellipsis,
                                modifier = Modifier.weight(1f).testTag(RequestTypeRuleTags.summary(name))
                            )
                            Icon(Icons.Default.ChevronRight, contentDescription = null, tint = LightGold, modifier = Modifier.size(18.dp))
                        }
                        if (name in missing) {
                            Text(
                                stringResource(Res.string.request_type_contracts_none),
                                color = ErrorOnNavy,
                                style = MaterialTheme.typography.bodySmall,
                                modifier = Modifier.padding(start = UnderLabel, bottom = 4.dp).testTag(RequestTypeRuleTags.missing(name))
                            )
                        }
                    }
                }
            }
        }
    }

    if (confirmRestore) {
        EvoConfirmDialog(
            title = stringResource(Res.string.request_types_restore_title),
            text = stringResource(Res.string.request_types_restore_text),
            confirmLabel = stringResource(Res.string.request_types_restore),
            icon = Icons.Default.Restore,
            onConfirm = {
                confirmRestore = false
                onEvent(SettingsEvent.DraftRestoreRecommendedRequests)
            },
            onDismiss = { confirmRestore = false }
        )
    }
}
